import React, { useState, useEffect, useRef } from 'react';
import { SessionManager } from '../../../utils/session-manager';
import AnalyticsService from '../../../analytics/AnalyticsService';
import { createSessionInfo } from '../../../analytics/payload';
import { logError } from '../../../utils/log-utils';
import Coverages from './components/Coverages/Coverages';
import styled from 'styled-components';
import Header from './components/Header/Header';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../../redux/api-v2/ProductsApi';
import { RootState } from '../../../redux/store';
import { AppDispatch } from '../../../redux/store';
import TravelInsurance from './components/TravelInsurance/TravelInsurance';
import Opinions from './components/Opinions/Opinions';
import ErgoSection from './components/ErgoSection/ErgoSection';
import PacksSection from './components/PacksSection/PacksSection';
import Downloads from './components/Downloads/Downloads';
import Blog from './components/Blog/Blog';
import Contact from './components/Contact/Contact';
import Footer from './components/Footer/Footer';
import Faq from './components/FAQ/Faq';
import TopBar from './components/TopBar/TopBar';
import { useWindowWidth } from '../../../hooks/useWindowWidth';
import { SectionRefs } from './types/navigation';
import MobileMenu from './components/MobileMenu/MobileMenu';

export const LandingPage = () => {
  const [isBlogLoaded, setIsBlogLoaded] = useState(false);
  const [isContactLoaded, setIsContactLoaded] = useState(false);
  const pageLoadStartTime = useRef<number>(new Date().getTime());
  const dispatch = useDispatch<AppDispatch>();

  const handleBlogLoaded = () => {
    setIsBlogLoaded(true);
  };

  useEffect(() => {
    dispatch(fetchProducts());

    if (!SessionManager.getUserId()) {
      SessionManager.startSession();
    }
    const pageLoadEndTime = new Date().getTime();
    const pageLoadTime = pageLoadEndTime - pageLoadStartTime.current;
    SessionManager.trackPage(window.location.href);
    SessionManager.incrementPageVisitCount();
    SessionManager.setDestinationTab('');
    SessionManager.setOriginTab('');
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.toString();
    let sessionInfo = createSessionInfo(pageLoadTime, query);
    AnalyticsService.sendLandingLog(sessionInfo).catch(error => {
      logError('Error sending page load analytics:', error);
    });
  }, []);

  const productsResponse = useSelector(
    (state: RootState) => state.products.products.response
  );
  const products = Array.isArray(productsResponse) ? productsResponse : [];
  const mainProduct =
    products.length > 0
      ? products.find(product => product.productId === 'MAIN2024')
      : null;
  const mainProductPriceWithEHIC = mainProduct?.priceWithEHIC;
  const mainProductPriceWithoutEHIC = mainProduct?.priceWithoutEHIC;

  const sectionRefs: SectionRefs = {
    WHYOCA: useRef<HTMLDivElement>(null),
    FAQS: useRef<HTMLDivElement>(null),
    BLOG: useRef<HTMLDivElement>(null),
    CONTACT: useRef<HTMLDivElement>(null)
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sectionId = urlParams.get('id');

    if (sectionId && sectionRefs[sectionId as keyof SectionRefs]?.current) {
      const timeout = setTimeout(() => {
        const element = sectionRefs[sectionId as keyof SectionRefs]?.current;
        if (element) {
          const topbarHeight = 120;
          const y =
            element.getBoundingClientRect().top +
            window.pageYOffset -
            topbarHeight;

          window.scrollTo({ top: y, behavior: 'smooth' });

          setTimeout(() => {
            if (Math.abs(window.scrollY - y) > 5) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 400);
        }

        window.history.replaceState({}, '', window.location.pathname);
      }, 250);

      return () => clearTimeout(timeout);
    }
  }, [isBlogLoaded, isContactLoaded]);

  const handleLandingElementClick = async (
    e: React.MouseEvent<HTMLDivElement>
  ) => {
    const target = e.target as HTMLElement;

    let currentElement: HTMLElement | null = target;
    let hasDropdownTracking = false;

    while (currentElement && !hasDropdownTracking) {
      if (
        currentElement.id &&
        (currentElement.id.startsWith('dropdown_') ||
          currentElement.id.startsWith('coverage_') ||
          currentElement.getAttribute('data-tracking') === 'true')
      ) {
        hasDropdownTracking = true;
      }
      currentElement = currentElement.parentElement;
    }

    if (
      target.id &&
      !hasDropdownTracking &&
      target.id !== 'FAQS' &&
      target.id !== 'coverages' &&
      target.id !== 'CONTACT'
    ) {
      SessionManager.setButtonId(target.id);

      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.toString();
      let sessionInfo = createSessionInfo(0, query);

      try {
        await AnalyticsService.sendLandingLog(sessionInfo);
      } catch (error) {
        logError('Error sending landing log:', error);
      }
    }
  };

  const width = useWindowWidth();

  return (
    <LandingGlobalStyles onClick={handleLandingElementClick}>
      {width >= 768 && <TopBar sectionRefs={sectionRefs} isLanding={true} />}
      {width < 768 && <MobileMenu sectionRefs={sectionRefs} isLanding={true} />}

      <Header
        mainProductPriceWithEHIC={mainProductPriceWithEHIC}
        mainProductPriceWithoutEHIC={mainProductPriceWithoutEHIC}
        ref={sectionRefs.WHYOCA}
      />
      <Coverages />
      <TravelInsurance
        mainProductPriceWithEHIC={mainProductPriceWithEHIC}
        mainProductPriceWithoutEHIC={mainProductPriceWithoutEHIC}
      />
      <Opinions />
      <ErgoSection />
      <Downloads />
      <PacksSection />
      <Blog ref={sectionRefs.BLOG} onLoad={handleBlogLoaded} />
      <Faq ref={sectionRefs.FAQS} />
      <Contact ref={sectionRefs.CONTACT} />
      <Footer />
    </LandingGlobalStyles>
  );
};

const LandingGlobalStyles = styled.div`
  font-family: 'League Spartan', sans-serif !important;
`;
